import { createContext, useEffect, useId, useRef, useState } from 'react';
import { ErrorBoundary } from '@datadog/browser-rum-react';

import { cn } from '@components/primitives/utilities';

import DiagramConnection from './DiagramConnection';
import DiagramGroup from './DiagramGroup';
import DiagramLogo from './DiagramLogo';
import DiagramNode from './DiagramNode';
import DiagramNodes from './DiagramNodes';
import DiagramRequest from './DiagramRequest';
import DiagramService from './DiagramService';

import css from './Nodes.module.css';
export const DiagramContext = createContext();

function Diagram({ children, className, direction = 'horizontal' }) {
  const id = useId();
  const [mounted, setMounted] = useState(false);
  const [resizing, setResizing] = useState(false);
  const timer = useRef(null);

  const root = useRef(null);

  // Remeasure on any size change of the diagram itself, not just the window,
  // so layout shifts (sidebars, grid reflow) redraw the connections too
  useEffect(() => {
    if (!root.current) return;
    let first = true;

    const observer = new ResizeObserver(() => {
      if (first) {
        first = false;
        return;
      }
      clearTimeout(timer.current);
      setResizing(true);

      timer.current = setTimeout(() => {
        setResizing(false);
      }, 300);
    });

    observer.observe(root.current);
    return () => {
      observer.disconnect();
      clearTimeout(timer.current);
    };
  }, [mounted]);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  const classes = cn(css.diagram, className);

  return (
    <DiagramContext.Provider value={{ id, resizing, direction }}>
      <div
        ref={root}
        id={`nodes-${id}`}
        className={classes}
        data-direction={direction}
      >
        {children}
      </div>
    </DiagramContext.Provider>
  );
}

Diagram.Node = DiagramNode;
Diagram.Group = DiagramGroup;
Diagram.Logo = DiagramLogo;
Diagram.Connection = DiagramConnection;
Diagram.Request = DiagramRequest;
Diagram.Service = DiagramService;
Diagram.Nodes = DiagramNodes;

function DiagramWithErrorBoundary(props) {
  return (
    <ErrorBoundary fallback={() => <div />}>
      <Diagram {...props} />
    </ErrorBoundary>
  );
}

Object.assign(DiagramWithErrorBoundary, Diagram);

export { DiagramWithErrorBoundary as Diagram };
